import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';

/** The listing surface a detail screen was opened from. */
export type NavigationOrigin = {
  pathname: string;
  search: string;
  scrollY: number;
};

type ReturnToOriginParams = {
  /** Where to go when there is no origin — a deep link, or a fresh tab. */
  fallback: string;
  /**
   * This screen's resolved base path, tab segment excluded — e.g.
   * `/admin/inbox/<quoteId>/<product>`. It says which locations still count as
   * "this screen", and forgetting the origin when it changes covers both a
   * different record and a different screen for the same record.
   */
  screen: string;
};

/**
 * Builds the router state that records where a detail screen was opened from.
 *
 * Deliberately not keyed `from`: the Prime 250 underwriter tree already carries
 * a `location.state.from` on these same routes, holding a bare path segment for
 * the post-delete redirect (see UWDeleteQuoteV2). Sharing the key would push
 * that redirect to `/admin/[object Object]`.
 *
 * Listing filters, sort and page all live in `location.search` (see
 * QueryParamsProvider), so carrying the whole location is enough to put the
 * underwriter back on the listing exactly as they left it.
 */
export const useCaptureOrigin = () => {
  const location = useLocation<{ navigationOrigin?: NavigationOrigin }>();
  const inheritedOrigin = toInAppOrigin(location.state?.navigationOrigin);

  return React.useCallback((): { navigationOrigin: NavigationOrigin } => {
    // A screen that already carries an origin is itself a detail screen — e.g.
    // opening a related quote from inside a workspace. Pass its origin along, so
    // back from the new screen still reaches the listing the underwriter started
    // on rather than the workspace they hopped from.
    if (inheritedOrigin) {
      return { navigationOrigin: inheritedOrigin };
    }

    return {
      navigationOrigin: {
        pathname: location.pathname,
        search: location.search,
        scrollY: window.scrollY,
      },
    };
  }, [inheritedOrigin, location.pathname, location.search]);
};

/**
 * Resolves a detail screen's back control: return to the listing the record was
 * opened from, or to `fallback` when there is nothing to return to.
 */
export const useReturnToOrigin = ({
  fallback,
  screen,
}: ReturnToOriginParams) => {
  const history = useHistory();
  const location = useLocation<
    { navigationOrigin?: NavigationOrigin } | undefined
  >();

  // Changing tab inside a detail screen pushes a bare path (TabSubrouter), which
  // drops `location.state`. Hold on to the origin we were opened with so the
  // back control still works after the underwriter has moved between tabs.
  const rememberedOrigin = React.useRef<NavigationOrigin | null>(null);
  const rememberedFor = React.useRef(screen);

  if (rememberedFor.current !== screen) {
    rememberedFor.current = screen;
    rememberedOrigin.current = null;
  }

  const arrivingOrigin = toReturnableOrigin(
    location.state?.navigationOrigin,
    location.pathname
  );
  if (arrivingOrigin) {
    rememberedOrigin.current = arrivingOrigin;
  }

  const origin = rememberedOrigin.current;

  // Put the origin back on the location the tab change stripped it from, so
  // everything else on the screen can read it the ordinary way. Guarded on still
  // being on this screen: on the way back out the location is already the
  // listing, and stamping an origin onto that would make the listing look like a
  // detail screen.
  const isStillOnThisScreen = location.pathname.startsWith(screen);

  React.useEffect(() => {
    if (!origin || !isStillOnThisScreen || location.state?.navigationOrigin) {
      return;
    }

    history.replace(
      { pathname: location.pathname, search: location.search },
      { ...location.state, navigationOrigin: origin }
    );
  }, [
    history,
    origin,
    isStillOnThisScreen,
    location.pathname,
    location.search,
    location.state,
  ]);

  return React.useCallback(() => {
    if (!origin) {
      history.push(fallback);
      return;
    }

    history.push(
      { pathname: origin.pathname, search: origin.search },
      { restoreScrollY: origin.scrollY }
    );
  }, [history, fallback, origin]);
};

/**
 * Only an in-app path is somewhere we will navigate back to. A protocol-relative
 * path ("//host") is an absolute URL to the browser, so it is rejected too.
 */
const toInAppOrigin = (from?: NavigationOrigin): NavigationOrigin | null => {
  const pathname = from?.pathname;

  if (!pathname?.startsWith('/') || pathname.startsWith('//')) {
    return null;
  }

  return {
    pathname,
    search: from?.search ?? '',
    scrollY: from?.scrollY ?? 0,
  };
};

/**
 * As above, but also rejects an origin pointing at the page we are on. The
 * related-quotes selector lists the current quote, so picking it would record a
 * screen as its own origin and leave back with nowhere to go.
 */
const toReturnableOrigin = (
  from: NavigationOrigin | undefined,
  currentPathname: string
): NavigationOrigin | null => {
  const origin = toInAppOrigin(from);

  if (!origin || origin.pathname === currentPathname) {
    return null;
  }

  return origin;
};
